import {
  useMutation,
  useQuery,
  type UseMutationOptions,
} from "@tanstack/react-query";
import useJwt from "@/components/checkout/hooks/useJwt";
import {
  addCreditCardToCustomer,
  deleteCustomerPaymentMethod,
  fetchCustomerPaymentMethods,
  updatePaymentMethod,
  type AddCreditCardData,
  type CreateCustomerPaymentMethodResponse,
  type CustomerPaymentMethodsResponse,
  type UpdatePaymentMethodInput,
  type UpdatePaymentMethodResponse,
} from "@/networking/payments.api";

// Stable shape: every key has the same length so exact-match cache ops
// (getQueryData / setQueryData) line up with the keys produced inside the
// query hook. Pass `countryCode = undefined` explicitly when there's no
// country dimension; pass `extra` for sub-scopes that need a different
// cache entry (e.g. a per-subscription PM list).
export const customerPaymentMethodsQueryKey = (
  jwt: string | null,
  countryCode?: string,
  extra: readonly unknown[] = [],
) => ["customer-payment-methods", jwt, countryCode, ...extra] as const;

type UseCustomerPaymentMethodsQueryArgs = {
  countryCode?: string;
  queryKey?: readonly unknown[];
  enabled?: boolean;
};

export function useCustomerPaymentMethodsQuery({
  countryCode,
  queryKey,
  enabled = true,
}: UseCustomerPaymentMethodsQueryArgs = {}) {
  const { jwt } = useJwt();
  return useQuery({
    queryKey: queryKey ?? customerPaymentMethodsQueryKey(jwt, countryCode),
    queryFn: () => fetchCustomerPaymentMethods(jwt as string, countryCode),
    enabled: enabled && !!jwt,
  });
}

export type AddCreditCardVariables = { paymentMethodData: AddCreditCardData };

export function useAddCreditCardMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<
      CreateCustomerPaymentMethodResponse,
      Error,
      AddCreditCardVariables,
      TContext
    >,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<
    CreateCustomerPaymentMethodResponse,
    Error,
    AddCreditCardVariables,
    TContext
  >({
    mutationFn: ({ paymentMethodData }) =>
      addCreditCardToCustomer(jwt as string, paymentMethodData),
    ...options,
  });
}

export function useDeletePaymentMethodMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<unknown, Error, string, TContext>,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<unknown, Error, string, TContext>({
    mutationFn: (paymentMethodId) =>
      deleteCustomerPaymentMethod(jwt as string, paymentMethodId),
    ...options,
  });
}

export type UpdatePaymentMethodVariables = {
  paymentMethodId: string;
  body?: UpdatePaymentMethodInput;
};

export function useUpdatePaymentMethodMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<
      UpdatePaymentMethodResponse,
      Error,
      UpdatePaymentMethodVariables,
      TContext
    >,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<
    UpdatePaymentMethodResponse,
    Error,
    UpdatePaymentMethodVariables,
    TContext
  >({
    mutationFn: ({ paymentMethodId, body }) =>
      updatePaymentMethod(jwt as string, paymentMethodId, body),
    ...options,
  });
}

export type { CustomerPaymentMethodsResponse };
